import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IAviaPlugin } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaPlugin';
import { AviaPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/AviaPlugin';
import { pauseAds } from 'tag/ts/tag/player/adapters/avia/plugins/pauseads/pauseAdsFactory';
import { TPluginConfigFactory } from 'tag/ts/tag/player/adapters/avia/types/TPluginConfigFactory';

/**
 * @copyright © Paramount Streaming
 * @description Smart Tag wrapper that registers the `pause-ads` avia-js plugin.
 *
 * Follows the same `AviaPlugin` pattern as `AdsPlugin`, `MetricsPlugin`, etc.:
 * - `package` points to the `pauseAds` factory function
 * - `shouldBeUsed()` gates activation on `skinConfig.PauseAd.enabled`
 * - `getFactoryParams()` supplies options at registration time
 *
 * Registered in `AviaPluginsManager.#allPluginsRoster`.
 *
 * Once the avia player initializes, the plugin API is retrievable via:
 * ```ts
 * player.getPlugin( 'pause-ads' )  // returns IPauseAdsPluginApi
 * ```
 *
 * `PauseAdService` uses that handle to delegate all pause ad requests
 * through the plugin's provider-based architecture.
 */
export class PauseAdsPlugin extends AviaPlugin implements IAviaPlugin {
	/** Avia-js plugin factory function that creates the `pause-ads` plugin instance. */
	package = pauseAds as unknown as TPluginConfigFactory;
	/** Unique namespace key used for plugin storage within the avia player adapter. */
	namespaceKey = 'plugin_pause_ads';

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'PauseAdsPlugin'
			}
		);
	}

	/**
	 * @description Determines whether the pause-ads plugin should be loaded for this session.
	 *
	 * Returns `true` (load) unless explicitly disabled via `skinConfig.PauseAd.enabled: false`.
	 * This means the plugin is available by default for both subscriber and anonymous users.
	 */
	shouldBeUsed(): boolean {
		const skinConfig = this.tag?.model?.config?.skin;
		return skinConfig?.PauseAd?.enabled !== false;
	}

	/**
	 * @description Returns configuration options passed to the `pauseAds()` factory at registration time.
	 * Extend this to inject runtime configuration into the plugin if needed.
	 */
	getFactoryParams() {
		return {
			...super.getFactoryParams()
		};
	}
}